import jwtDecode from 'jwt-decode';

import { ContentTypes } from '@invoice-simple/common';

interface TokenStore {
  /** Resolves the cached token synchronously while it is still valid, so the common path is free. */
  getToken: () => Promise<string> | string;
  clearToken: () => void;
}

interface TokenStoreConfig {
  /**
   * A function rather than a string because the is-payments url comes from `process.env`, which CRA
   * inlines at build time but tests set per case — reading it at call time keeps both working.
   */
  getExchangeUrl: () => string;
  /**
   * Injected rather than read from `UserModel` here: `UserModel` transitively imports `Accounts`,
   * which imports every api client, so importing it from this module would make it part of that
   * cycle and leave `createTokenStore` undefined depending on which module was entered first.
   */
  getParseToken: () => string | undefined;
}

function isTokenExpired(token: string) {
  const decoded: { exp: number } = jwtDecode(token);
  // Add some small buffer room of 10 seconds
  return decoded.exp - Date.now() / 1000 <= 10;
}

/**
 * Every payments service issues its own JWT from the parse session token, and the exchange plus
 * cache-until-expiry logic is identical for all of them, so each token module is a one-line wrapper
 * around this.
 */
export function createTokenStore({ getExchangeUrl, getParseToken }: TokenStoreConfig): TokenStore {
  let token: string | null = null;

  async function exchangeToken(parseToken: string): Promise<{ token: string }> {
    const url = getExchangeUrl();
    try {
      const res = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': ContentTypes.APP_JSON
        },
        body: JSON.stringify({ parseToken })
      });
      return await res.json();
    } catch (err) {
      throw new Error(`Unexpected error exchanging token: ${err.message}`);
    }
  }

  async function generateAndPersistNewToken(): Promise<string> {
    try {
      const parseToken = getParseToken();
      if (!parseToken) {
        throw new Error('Missing parse token');
      }
      const exchangeResult = await exchangeToken(parseToken);
      token = exchangeResult.token;
      return token;
    } catch (error) {
      throw new Error(error.message);
    }
  }

  return {
    getToken: () => {
      try {
        if (!token || isTokenExpired(token)) {
          return generateAndPersistNewToken();
        }
        return token;
      } catch (err) {
        throw new Error(err.message);
      }
    },
    clearToken: () => {
      token = null;
    }
  };
}
